'use client';

/**
 * Аккордеон FAQ: категории → вопросы.
 *
 * Открытый вопрос хранится по id, а не по позиции. Позиция не переживает ни
 * разбиения на категории, ни перегенерации страницы: вопрос, добавленный в
 * админке выше открытого, сдвинул бы индекс, и раскрытым оказался бы соседний.
 *
 * Ответ приходит готовым узлом: markdown рендерит FaqAnswer на сервере, сам
 * аккордеон про markdown не знает и парсер в браузер не тянет.
 *
 * Заголовки категорий появляются, только когда категорий больше одной:
 * единственный заголовок над списком лишь повторял бы заголовок страницы, и
 * такой FAQ выглядит как раньше. Уровень заголовка вопроса идёт следом, чтобы
 * структура не перескакивала через уровень: h1 страницы → h2 категории → h3
 * вопроса, а без категорий — h1 → h2 вопроса.
 *
 * У каждого вопроса якорь `#q-<id>`: на него ссылаются ответы других
 * вопросов (faq-question: → #q-<id>, см. FaqAnswer). Переход по такому адресу
 * раскрывает вопрос и прокручивает к нему — при загрузке, по hashchange и по
 * клику на ссылку. Клик ловится отдельно: повторный переход на тот же # (вопрос
 * успели свернуть) hashchange не порождает.
 *
 * Переход ставит вопрос на середину окна (или к началу, если с ответом он выше
 * окна — lib/faq/reveal) и коротко подсвечивает его. Ручное раскрытие кликом
 * не подсвечивается.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { FadeIn } from '@/components/common/FadeIn';
import { questionIdFromHash } from '@/lib/faq/anchors';
import {
  nextFlash,
  questionCard,
  revealQuestion,
  syncQuestionHash,
  type QuestionFlash,
} from '@/lib/faq/reveal';


export interface FaqAccordionItem {
  id: string;
  question: string;
  answer: ReactNode;
}

export interface FaqAccordionSection {
  id: string;
  title: string;
  items: FaqAccordionItem[];
}

interface FaqAccordionProps {
  sections: FaqAccordionSection[];
}

export function FaqAccordion({ sections }: FaqAccordionProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  // Подсветка вопроса, открытого переходом по ссылке (только им, не кликом)
  const [flash, setFlash] = useState<QuestionFlash | null>(null);
  // Вопрос, к которому надо прокрутить, когда его панель раскроется. Раньше
  // крутить нельзя: середина или начало решается по высоте вместе с ответом, а
  // ранее открытый вопрос в это же время сворачивается — если он выше, цель
  // уезжает вверх на высоту его ответа
  const pendingScroll = useRef<string | null>(null);
  // Открытый вопрос для openQuestion: уже раскрытый анимации не даст, к нему
  // крутим сразу. Через ref, а не зависимостью — иначе openQuestion менялся бы
  // с каждым раскрытием и эффект hashchange переподписывался бы вместе с ним
  const openIdRef = useRef<string | null>(null);
  useEffect(() => {
    openIdRef.current = openId;
  }, [openId]);

  // id в нижнем регистре → id как в данных: адрес приходит в любом регистре
  const itemIds = useMemo(
    () =>
      new Map(
        sections.flatMap((section) => section.items.map((item) => [item.id.toLowerCase(), item.id]))
      ),
    [sections]
  );

  // Прокрутить к вопросу и подсветить его, когда он встанет на место: пока идёт
  // прокрутка, вопрос ещё за краем экрана, и подсветку никто бы не увидел
  const reveal = useCallback((itemId: string) => {
    revealQuestion(questionCard(itemId), () => setFlash((prev) => nextFlash(prev, itemId)));
  }, []);

  const openQuestion = useCallback(
    (id: string) => {
      const itemId = itemIds.get(id.toLowerCase());
      if (!itemId) return;
      setOpenId(itemId);
      if (openIdRef.current !== itemId) {
        // Прокрутит onAnimationComplete панели, когда ответ раскроется
        pendingScroll.current = itemId;
        return;
      }
      // Уже раскрыт: два кадра на отрисовку — и сразу к нему
      pendingScroll.current = null;
      requestAnimationFrame(() => requestAnimationFrame(() => reveal(itemId)));
    },
    [itemIds, reveal]
  );

  useEffect(() => {
    const openFromHash = () => {
      const id = questionIdFromHash(window.location.hash);
      if (id) openQuestion(id);
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, [openQuestion]);

  // Ссылка на вопрос внутри ответа. Штатный переход браузера отменяем: он
  // сразу прыгает к якорю (вопрос у верхнего края), а после раскрытия ответа
  // наша прокрутка вела бы его обратно к середине — страница скакала. Адрес
  // меняем сами через pushState («назад» вернёт; hashchange он не шлёт, так что
  // вопрос открывается ровно один раз — и на повторном клике по тому же #).
  // Клик с модификатором открывает ссылку в новой вкладке — эту не трогаем
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    if (event.button !== 0 || modified || !(event.target instanceof Element)) return;
    const href = event.target.closest('a[href^="#q-"]')?.getAttribute('href');
    const id = questionIdFromHash(href);
    if (!href || !id) return;
    event.preventDefault();
    if (window.location.hash !== href) window.history.pushState(null, '', href);
    openQuestion(id);
  };

  const titled = sections.length > 1;
  const Group = titled ? 'section' : 'div';
  const QuestionHeading = titled ? 'h3' : 'h2';

  // Сквозной номер для каскада появления: продолжается через категории
  let position = 0;

  return (
    // Делегированный обработчик: ссылки рисует серверный FaqAnswer, своих
    // обработчиков у них нет. Клавиатура тоже доходит сюда — Enter на ссылке
    // порождает click
    <div className="space-y-12" onClick={handleClick}>
      {sections.map((section) => {
        const headingId = `faq-section-${section.id}`;

        return (
          <Group key={section.id} aria-labelledby={titled ? headingId : undefined}>
            {titled && (
              <h2 id={headingId} className="mb-2 text-2xl font-bold text-text-primary">
                {section.title}
              </h2>
            )}

            <div className="divide-y divide-border-base">
              {section.items.map((item) => {
                const isOpen = openId === item.id;
                const buttonId = `faq-question-${item.id}`;
                const panelId = `faq-answer-${item.id}`;
                const delay = position++ * 0.07;

                return (
                  <FadeIn key={item.id} delay={delay}>
                    {/* Карточка вопроса — цель ссылки #q-<id>. Атрибут
                        (QUESTION_CARD_ATTR), а не id: см. lib/faq/reveal. Отступ сверху — под липкую шапку
                        сайта, как у заголовков статей (.article-body h2).
                        isolate: слой подсветки (поверх, полупрозрачный,
                        клики не ловит) не выходит за свой контекст наложения */}
                    <div
                      data-faq-question={item.id.toLowerCase()}
                      className="relative isolate scroll-mt-24"
                    >
                      {flash?.id === item.id && (
                        // Новый ключ на каждый переход — анимация идёт заново.
                        // Отыграла — убираем, чтобы не повторилась при
                        // перерисовке. Поля по бокам — чтобы фон не прилипал к тексту
                        <span
                          key={flash.n}
                          aria-hidden="true"
                          data-question-flash=""
                          onAnimationEnd={() => setFlash((f) => (f?.n === flash.n ? null : f))}
                          className="faq-flash pointer-events-none absolute inset-y-0 -inset-x-3 z-10 rounded-lg"
                        />
                      )}
                      <QuestionHeading>
                        <button
                          type="button"
                          id={buttonId}
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                          onClick={() => {
                            // Рукой переключили — докрутка от ссылки больше не нужна
                            pendingScroll.current = null;
                            setOpenId(isOpen ? null : item.id);
                            syncQuestionHash(item.id, !isOpen);
                          }}
                          className="flex w-full items-center justify-between py-5 text-left cursor-pointer"
                        >
                          <span
                            className={`pr-4 text-lg font-medium ${isOpen ? 'text-primary' : 'text-text-primary'}`}
                          >
                            {item.question}
                          </span>
                          <motion.span
                            animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                            className={`shrink-0 ${isOpen ? 'text-primary' : 'text-text-secondary'}`}
                          >
                            <ChevronDown size={20} aria-hidden="true" />
                          </motion.span>
                        </button>
                      </QuestionHeading>

                      {/* Панель в DOM всегда, закрытая — высотой 0. Раньше она
                          монтировалась только по клику, и в HTML страницы не
                          было ни одного ответа: Google и ИИ-поиск видели вопросы
                          без ответов, а разметка FAQPage описывала невидимый
                          текст. aria-hidden и inert убирают закрытый ответ от
                          скринридера и Tab: ссылки внутри не должны ловить фокус. */}
                      <motion.div
                        id={panelId}
                        role="region"
                        aria-labelledby={buttonId}
                        aria-hidden={!isOpen}
                        inert={!isOpen}
                        initial={false}
                        animate={isOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onAnimationComplete={() => {
                          if (!isOpen || pendingScroll.current !== item.id) return;
                          pendingScroll.current = null;
                          reveal(item.id);
                        }}
                        className="overflow-hidden"
                      >
                        <div className="pb-5">{item.answer}</div>
                      </motion.div>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </Group>
        );
      })}
    </div>
  );
}
